"use client"

import React, { useState, useEffect, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import axios from "axios"
import {
  Container,
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Grid,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  FormHelperText,
} from "@mui/material"
import {
  Assignment,
  RateReview,
  Feedback,
  Timeline,
  Star,
  StarBorder,
  AccountTree,
  Visibility,
  Email,
  Person,
  DateRange,
  Send,
  Search,
  FilterList,
} from "@mui/icons-material"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers"
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_URL}`,
  headers: {
    "Content-Type": "application/json",
  },
})

const PerformanceReviewsTab = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [reviews, setReviews] = useState([])
  const [employees, setEmployees] = useState([])
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })
  const [expandedReviewId, setExpandedReviewId] = useState(null)
  const [loading, setLoading] = useState({
    reviews: false,
    employees: false,
    submit: false,
  })

  const [newReview, setNewReview] = useState({
    employee_id: "",
    employee_email: "",
    reviewer: "",
    reviewDate: new Date().toISOString().split("T")[0],
    type: "Annual",
    rating: "",
    status: "Scheduled",
    progress: 0,
    comments: "",
  })

  // State for showing employee-specific reviews
  const [selectedEmployeeForView, setSelectedEmployeeForView] = useState("")
  const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await Promise.all([fetchEmployees(), fetchReviews()])
      } catch (error) {
        //console.error("Error fetching initial data:", error)
      }
    }
    fetchInitialData()
  }, [])

  // Auto-populate email when employee is selected
  useEffect(() => {
    if (newReview.employee_id) {
      const selectedEmployee = employees.find((emp) => emp.employee_id === newReview.employee_id)
      if (selectedEmployee) {
        setNewReview((prev) => ({
          ...prev,
          employee_email: selectedEmployee.email || "",
        }))
      }
    } else {
      setNewReview((prev) => ({
        ...prev,
        employee_email: "",
      }))
    }
  }, [newReview.employee_id, employees])

  const handleTabChange = (event, newValue) => {
    navigate(newValue)
  }

  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity })
  }

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const fetchReviews = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    let employee_id = payload.employee_id;
    if (!employee_id) {
      try {
        const userRes = await api.get(`/api/user/current`, { headers: { Authorization: `Bearer ${token}` } });
        if (userRes.data && userRes.data.employee_id) employee_id = userRes.data.employee_id;
      } catch (e) {}
    }
      setLoading((prev) => ({ ...prev, reviews: true }))
      const response = await api.get(`/api/reviews?employee_id=${employee_id || ''}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      )
      setReviews(response.data || [])
      
      try {
        const cmpRes = await api.get(`/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (cmpRes.data && cmpRes.data.data) {
          setCompanyConfig({
            dateFormat: cmpRes.data.data.dateFormat || 'DD/MM/YYYY',
            timezone: cmpRes.data.data.timezone || 'Asia/Kolkata'
          });
        }
      } catch(e) {}
      
      return response.data || []
    } catch (error) {
      //console.error("Error fetching reviews:", error)
      openSnackbar(`Failed to load reviews: ${error.message}`, "error")
      setReviews([])
      return []
    } finally {
      setLoading((prev) => ({ ...prev, reviews: false }))
    }
  }

  const fetchEmployees = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    let employee_id = payload.employee_id;
    if (!employee_id) {
      try {
        const userRes = await api.get(`/api/user/current`, { headers: { Authorization: `Bearer ${token}` } });
        if (userRes.data && userRes.data.employee_id) employee_id = userRes.data.employee_id;
      } catch (e) {}
    }
      setLoading((prev) => ({ ...prev, employees: true }))
      const response = await api.get(`/api/reviews/employees?employee_id=${employee_id || ''}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      )

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid employee data received from server")
      }

      const employeeData = response.data
      const normalizedEmployees = employeeData.map((emp) => ({
        employee_id: emp.employee_id || emp.id || emp._id,
        firstName: emp.firstName || emp.first_name || emp.fname || "Unknown",
        lastName: emp.lastName || emp.last_name || emp.lname || "",
        email: emp.email || "",
        department: emp.department || emp.dept || "General",
        position: emp.position || emp.designation || emp.role || "",
      }))

      setEmployees(normalizedEmployees)
      return normalizedEmployees
    } catch (error) {
      //console.error("Error fetching employees:", error)
      openSnackbar(`Failed to load employees: ${error.message}`, "error")
      setEmployees([])
      return []
    } finally {
      setLoading((prev) => ({ ...prev, employees: false }))
    }
  }

  const addReview = async () => {
    try {
      if (!newReview.employee_id || !newReview.reviewDate || !newReview.type) {
        openSnackbar("Please fill in all required fields", "error");
        return;
      }
  
      setLoading((prev) => ({ ...prev, submit: true }));
  
      // Extract company_id from JWT
      const token = localStorage.getItem('token');
      if (!token) throw new Error("No authentication token found.");
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
  
      // Send POST request with company_id in query
      const response = await api.post(
        `/api/reviews`,
        newReview,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
  
      await fetchReviews();
  
      setNewReview({
        employee_id: "",
        employee_email: "",
        reviewer: "",
        reviewDate: new Date().toISOString().split("T")[0],
        type: "Annual",
        rating: "",
        status: "Scheduled",
        progress: 0,
        comments: "",
      });
  
      openSnackbar("Review scheduled successfully and email notification sent!", "success");
    } catch (error) {
      openSnackbar(`Failed to create review: ${error.message}`, "error");
    } finally {
      setLoading((prev) => ({ ...prev, submit: false }));
    }
  };
  

  const toggleExpandReview = (reviewId) => {
    setExpandedReviewId(expandedReviewId === reviewId ? null : reviewId)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Scheduled":
        return "info"
      case "In Progress":
        return "warning"
      case "Completed":
        return "success"
      case "Overdue":
        return "error"
      default:
        return "default"
    }
  }

  const getStars = (rating) => {
    const ratingNum = Number.parseInt(rating) || 0
    return [...Array(5)].map((_, i) =>
      i < ratingNum ? <Star key={i} color="primary" fontSize="small" /> : <StarBorder key={i} fontSize="small" />,
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return dayjs(dateString).tz(companyConfig.timezone).format(companyConfig.dateFormat)
  }

  const getEmployeeName = (employeeId) => {
    const employee = employees.find((emp) => emp.employee_id === employeeId)
    if (!employee) return "Unknown Employee"

    // Handle cases where lastName might be empty or undefined
    const lastName = employee.lastName && employee.lastName.trim() ? employee.lastName : ""
    return lastName ? `${employee.firstName} ${lastName}` : employee.firstName
  }

  // Filter reviews based on selected employee for viewing - only show when employee is selected
  const filteredReviewsForDisplay = useMemo(() => {
    if (!reviews || reviews.length === 0) return []

    // Only show reviews if an employee is specifically selected
    if (!selectedEmployeeForView) return []

    return reviews.filter((review) => review.employee_id === selectedEmployeeForView)
  }, [reviews, selectedEmployeeForView])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth={false} sx={{ maxWidth: 1500, p: 3, boxShadow: 3, mx: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" gutterBottom  fontWeight="bold">
            Performance Management 
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ mb: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={location.pathname}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                backgroundColor: "#f5f5f5",
                "& .MuiTab-root": { fontWeight: "medium", py: 2 },
              }}
            >
              <Tab icon={<Assignment />} label="Goals & Objectives" value="/performance" />
              <Tab icon={<RateReview />} label="Performance Reviews" value="/review" />
              <Tab icon={<Feedback />} label="Feedback System" value="/feedback" />
              <Tab icon={<Timeline />} label="Improvement Plans" value="/improve" />
              <Tab icon={<AccountTree />} label="Succession Planning" value="/succession" />
              <Tab icon={<Visibility />} label="View Status" value="/performancestatus" />
            </Tabs>
          </Box>
        </Paper>

        {location.pathname === "/review" && (
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              {/* Left side - Create Review */}
              <Grid item xs={12} md={4}>
                <Card elevation={3}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", color: "#1976d2" }}
                    >
                      <RateReview sx={{ mr: 1 }} /> Schedule Performance Review
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Tooltip title="Select the employee for this performance review" arrow>
                      <FormControl fullWidth margin="normal" required>
                        <InputLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Person sx={{ mr: 0.5, fontSize: 16 }} />
                            Employee Name *
                          </Box>
                        </InputLabel>
                        <Select
                          value={newReview.employee_id}
                          onChange={(e) => setNewReview({ ...newReview, employee_id: e.target.value })}
                          label="Employee Name"
                          disabled={loading.employees}
                          
                        >
                          <MenuItem value="">
                            <em>{loading.employees ? "Loading employees..." : "Select Employee"}</em>
                          </MenuItem>
                          {employees.length > 0
                            ? employees.map((employee) => (
                                <MenuItem key={employee.employee_id} value={employee.employee_id}>
                                  {getEmployeeName(employee.employee_id)}
                                  {/* {employee.department && ` - ${employee.department}`} */}
                                </MenuItem>
                              ))
                            : !loading.employees && (
                                <MenuItem disabled>
                                  <em>No employees found</em>
                                </MenuItem>
                              )}
                        </Select>
                        {loading.employees && <LinearProgress sx={{ mt: 1 }} />}
                      </FormControl>
                    </Tooltip>

                    {/* Employee Email Field */}
                    <Tooltip title=" email address (automatically fetched from database)" arrow>
                      <TextField
                        fullWidth
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Email sx={{ mr: 0.5, fontSize: 16 }} />
                            Email
                          </Box>
                        }
                        value={newReview.employee_email}
                        margin="normal"
                        disabled
                     helperText="Email is automatically fetching based on employee name"
                        InputProps={{
                          style: { backgroundColor: "#f5f5f5" },
                        }}
                      />
                    </Tooltip>

                    <TextField
                      fullWidth
                      label="Reviewer"
                      value={newReview.reviewer}
                      onChange={(e) => setNewReview({ ...newReview, reviewer: e.target.value })}
                      margin="normal"
                      placeholder="Enter reviewer's name"
                    />

                    <Tooltip title="Set the date for the performance review" arrow>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <DateRange sx={{ mr: 0.5, fontSize: 16 }} />
                            Review Date *
                          </Box>
                        }
                        value={newReview.reviewDate ? dayjs(newReview.reviewDate) : null}
                        onChange={(date) =>
                          setNewReview({ ...newReview, reviewDate: date ? date.toISOString().split("T")[0] : null })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            margin="normal"
                            required
                            helperText="Select the date for the review"
                          />
                        )}
                        sx={{ marginTop: "17px" }}
                      />
                      </LocalizationProvider>
                    </Tooltip>

                    <FormControl fullWidth margin="normal">
                      <InputLabel>Review Type</InputLabel>
                      <Select
                        value={newReview.type}
                        onChange={(e) => setNewReview({ ...newReview, type: e.target.value })}
                        label="Review Type"
                      >
                        <MenuItem value="Annual">Annual Review</MenuItem>
                        <MenuItem value="Quarterly">Quarterly Review</MenuItem>
                        <MenuItem value="Midyear">Mid-year Review</MenuItem>
                        <MenuItem value="Probation">Probation Completion</MenuItem>
                        <MenuItem value="Project">Project Completion</MenuItem>
                        <MenuItem value="360">360 Review</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                      <InputLabel>Rating</InputLabel>
                      <Select
                        value={newReview.rating}
                        onChange={(e) => setNewReview({ ...newReview, rating: e.target.value })}
                        label="Rating"
                      >
                        <MenuItem value="">
                          <em>Select Rating</em>
                        </MenuItem>
                        <MenuItem value="1">1 - Needs Improvement</MenuItem>
                        <MenuItem value="2">2 - Progressing</MenuItem>
                        <MenuItem value="3">3 - Meets Expectations</MenuItem>
                        <MenuItem value="4">4 - Exceeds Expectations</MenuItem>
                        <MenuItem value="5">5 - Outstanding</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Comments"
                      value={newReview.comments}
                      onChange={(e) => setNewReview({ ...newReview, comments: e.target.value })}
                      margin="normal"
                      placeholder="Add any comments or notes about this review..."
                    />

                    <Box sx={{ mt: 3, textAlign: "center" }}>
                      <Tooltip title="Schedule review and send email notification to employee" arrow>
                        <Button
                          variant="contained"
                          startIcon={loading.submit ? <CircularProgress size={20} /> : <Send />}
                          onClick={addReview}
                          disabled={loading.submit}
                          sx={{
                            bgcolor: "#1976d2",
                            color: "white",
                            px: 4,
                            py: 1.5,
                            fontSize: "1rem",
                            fontWeight: "medium",
                          }}
                        >
                          {loading.submit ? "Scheduling..." : "Submit"}
                        </Button>
                      </Tooltip>
                      <FormHelperText sx={{ mt: 1, textAlign: "center", fontSize: "0.875rem" }}>
                        This will create the review and send an email notification to the employee
                      </FormHelperText>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Right side - Employee Reviews */}
              <Grid item xs={12} md={8}>
                <Paper elevation={3}>
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      bgcolor: "#f5f5f5",
                    }}
                  >
                    <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
                      <Search sx={{ mr: 1 }} /> Employee Reviews
                      {selectedEmployeeForView && ` (${filteredReviewsForDisplay.length})`}
                    </Typography>
                    <Tooltip title="Select an employee to view their reviews" arrow>
                      <FormControl size="small" sx={{ minWidth: 300 }}>
                        <InputLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <FilterList sx={{ mr: 0.5, fontSize: 16 }} />
                            Select Employee to View Reviews
                          </Box>
                        </InputLabel>
                        <Select
                          value={selectedEmployeeForView}
                          onChange={(e) => setSelectedEmployeeForView(e.target.value)}
                          label="Select Employee to View Reviews"
                        >
                          <MenuItem value="">
                            <em>Choose an employee</em>
                          </MenuItem>
                          {employees.map((employee) => (
                            <MenuItem key={employee.employee_id} value={employee.employee_id}>
                              {getEmployeeName(employee.employee_id)}
                              
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Tooltip>
                  </Box>

                  <Divider />

                  {loading.reviews ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Loading reviews...
                      </Typography>
                    </Box>
                  ) : !selectedEmployeeForView ? (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                      <Search sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Select an Employee
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Choose an employee from the dropdown above to view their performance reviews.
                      </Typography>
                    </Box>
                  ) : filteredReviewsForDisplay.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Typography variant="body1">
                        No reviews found for {getEmployeeName(selectedEmployeeForView)}.
                      </Typography>
                    </Box>
                  ) : (
                    <List sx={{ maxHeight: "650px", overflow: "auto", p: 0 }}>
                      {filteredReviewsForDisplay.map((review) => (
                        <React.Fragment key={review.id}>
                          <ListItem
                            button
                            onClick={() => toggleExpandReview(review.id)}
                            sx={{
                              "&:hover": { bgcolor: "#f9f9f9" },
                              p: 2,
                              borderLeft: "4px solid",
                              borderLeftColor:
                                getStatusColor(review.status) === "error"
                                  ? "#f44336"
                                  : getStatusColor(review.status) === "warning"
                                    ? "#ff9800"
                                    : "#2196f3",
                              mb: 1,
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <Typography variant="subtitle1" fontWeight="medium">
                                    {review.personal?.firstName && review.personal?.lastName
                                      ? `${review.personal.firstName} ${review.personal.lastName}`
                                      : review.personal?.firstName || "Employee"}{" "}
                                    (ID: {review.employee_id})
                                  </Typography>
                                  <Chip
                                    label={review.status || "Scheduled"}
                                    color={getStatusColor(review.status || "Scheduled")}
                                    size="small"
                                  />
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      {review.type} Review • {formatDate(review.reviewDate)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Reviewer: {review.reviewer || "Not assigned"}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ mt: 1, mb: 1 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                      <Typography variant="caption">Progress</Typography>
                                      <Typography variant="caption">{review.progress || 0}%</Typography>
                                    </Box>
                                    <LinearProgress
                                      variant="determinate"
                                      value={review.progress || 0}
                                      sx={{ height: 8, borderRadius: 1 }}
                                    />
                                  </Box>

                                  {review.rating && <Box sx={{ mt: 1 }}>{getStars(review.rating)}</Box>}
                                  {review.comments && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                      <strong>Comments:</strong> {review.comments}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>

                          {expandedReviewId === review.id && (
                            <Box sx={{ px: 4, pb: 2 }}>
                              <Paper elevation={2} sx={{ p: 2, bgcolor: "#f9f9f9" }}>
                                <Grid container spacing={2}>
                                  {review.progressUpdates?.length > 0 && (
                                    <Grid item xs={12}>
                                      <Typography variant="h6" gutterBottom>
                                        Progress History
                                      </Typography>
                                      {review.progressUpdates.map((update, idx) => (
                                        <Box key={idx} sx={{ mb: 2, p: 2, bgcolor: "white", borderRadius: 1 }}>
                                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                            <Typography variant="caption" color="textSecondary">
                                              {new Date(update.date).toLocaleString("en-US", {
                                                year: "numeric",
                                                month: "short",
                                                day: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                hour12: true,
                                              })}
                                            </Typography>
                                          </Box>
                                          <Typography variant="body2">{update.note}</Typography>
                                          <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                                            <Chip
                                              label={update.status}
                                              size="small"
                                              color={getStatusColor(update.status)}
                                            />
                                            <Chip
                                              label={`${update.progress}%`}
                                              size="small"
                                              color={
                                                update.progress >= 75
                                                  ? "success"
                                                  : update.progress >= 50
                                                    ? "warning"
                                                    : "error"
                                              }
                                            />
                                          </Box>
                                        </Box>
                                      ))}
                                    </Grid>
                                  )}
                                </Grid>
                              </Paper>
                            </Box>
                          )}
                          <Divider />
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  )
}

export default PerformanceReviewsTab